import api from '../utils/axios';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';

// Proper Leads Service for managing leads using dedicated leads endpoint
// Connected to MongoDB database via API
export class LeadsService {

  // Create a new lead
  static async createLead(leadData) {
    try {
      console.log('🔄 Creating lead with data:', leadData);
      console.log('📅 Date from form:', leadData.date);
      console.log('🌐 API Base URL:', API_BASE_URL);
      console.log('🎯 Making request to:', `${API_BASE_URL}/leads`);

      // Simplified payload for local backend with proper field mapping based on MongoDB schema
      const leadPayload = {
        title: leadData.title || 'New Lead',
        name: leadData.customerName || leadData.contactName || leadData.name,
        email: leadData.email,
        phone: leadData.contactNumber || leadData.phone,
        companyName: leadData.customerCompany || leadData.companyName,
        company_name: leadData.customerCompany || leadData.companyName,
        source: leadData.sourceOfLead || leadData.source || 'website',
        budget: leadData.budget,
        status: leadData.status || 'new',
        priority: leadData.priority || 'medium',
        notes: leadData.notes || '',
        value: leadData.value || 0,
        address: leadData.address,
        productOfInterest: leadData.productOfInterest,
        sector: leadData.sector,
        productCategory: leadData.productCategory,
        sourceOfLead: leadData.sourceOfLead,
        date: leadData.date, // Include the selected date from form
        department: [],
        teamMember: [],
        images: [],
        tags: [],
        followUps: [],
        autoCaptured: false,
        // Remove hardcoded createdAt and lastContact - let backend handle these
        // Include any custom fields from the form
        ...Object.keys(leadData).reduce((acc, key) => {
          if (!['customerName', 'contactName', 'contactNumber', 'customerCompany', 'title', 'name', 'email', 'phone', 'companyName', 'company_name', 'source', 'budget', 'status', 'priority', 'notes', 'value', 'address', 'productOfInterest', 'sector', 'productCategory', 'sourceOfLead', 'date'].includes(key)) {
            acc[key] = leadData[key];
          }
          return acc;
        }, {})
      };

      console.log('📤 Sending lead payload:', leadPayload);
      const response = await api.post(API_ENDPOINTS.LEADS, leadPayload);
      console.log('✅ Lead created successfully:', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error creating lead:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      return { success: false, error: error.response?.data?.message || 'Failed to create lead. Please ensure the leads API is deployed.' };
    }
  }

  // Get all leads
  static async getAllLeads() {
    try {
      console.log('🔍 Fetching leads from:', API_ENDPOINTS.LEADS);
      // Request all leads without pagination limit
      const response = await api.get(API_ENDPOINTS.LEADS);
      console.log('📊 Leads API response:', response);

      // Extract leads from backend response
      const responseData = response.data;
      console.log('📋 Response data structure:', {
        hasLeads: 'leads' in responseData,
        hasData: 'data' in responseData,
        isArray: Array.isArray(responseData),
        keys: Object.keys(responseData)
      });

      let leads = [];

      // Handle different response formats - backend returns { data: { leads: [...] } }
      if (responseData.data && responseData.data.leads && Array.isArray(responseData.data.leads)) {
        leads = responseData.data.leads;
        console.log('✅ Using leads array from response.data.leads');
      } else if (responseData.leads && Array.isArray(responseData.leads)) {
        leads = responseData.leads;
        console.log('✅ Using leads array from response.leads');
      } else if (responseData.data && Array.isArray(responseData.data)) {
        leads = responseData.data;
        console.log('✅ Using data array from response');
      } else if (Array.isArray(responseData)) {
        leads = responseData;
        console.log('✅ Using direct array response');
      } else {
        console.warn('⚠️ Unexpected response format, returning empty array');
        leads = [];
      }

      console.log(`📊 Found ${leads.length} leads`);
      return { success: true, data: leads };
    } catch (error) {
      console.error('❌ Error fetching leads:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.config?.headers
      });

      return {
        success: false,
        error: error.response?.data?.message ||
          `Failed to fetch leads: ${error.message}. Please ensure the leads API is running and accessible.`
      };
    }
  }

  // Get a single lead by ID
  static async getLeadById(leadId) {
    console.log('LeadsService: Fetching lead by ID:', leadId);
    console.log('LeadsService: API endpoint:', API_ENDPOINTS.LEAD_BY_ID(leadId));
    try {
      const response = await api.get(API_ENDPOINTS.LEAD_BY_ID(leadId));
      console.log('LeadsService: Lead API response:', response);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('LeadsService: Error fetching lead:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch lead' };
    }
  }

  // Update a lead
  static async updateLead(leadId, leadData) {
    console.log('🔄 Updating lead with data:', leadData);
    console.log('📅 Date from form:', leadData.date);
    console.log('📤 Lead payload will include date:', leadData.date);

    const leadPayload = {
      // Map frontend fields to backend schema
      customerName: leadData.customerName || leadData.name,
      name: leadData.customerName || leadData.name,
      email: leadData.email,
      phone: leadData.contactNumber || leadData.phone,
      contactNumber: leadData.contactNumber || leadData.phone,
      customerCompany: leadData.customerCompany || leadData.company,
      company: leadData.customerCompany || leadData.company,
      source: leadData.sourceOfLead || leadData.source,
      sourceOfLead: leadData.sourceOfLead || leadData.source,
      status: leadData.status,
      priority: leadData.priority || 'medium',
      value: leadData.value || 0,
      budget: leadData.budget,
      notes: leadData.notes || '',
      address: leadData.address,
      productOfInterest: leadData.productOfInterest,
      sector: leadData.sector,
      productCategory: leadData.productCategory,
      date: leadData.date, // Include the updated date
      lastContact: new Date().toISOString(),
      // Include any custom fields from the form
      ...Object.keys(leadData).reduce((acc, key) => {
        if (!['customerName', 'name', 'email', 'contactNumber', 'phone', 'customerCompany', 'company', 'sourceOfLead', 'source', 'status', 'priority', 'value', 'budget', 'notes', 'address', 'productOfInterest', 'sector', 'productCategory', 'date'].includes(key)) {
          acc[key] = leadData[key];
        }
        return acc;
      }, {})
    };

    console.log('📤 Final payload being sent to backend:', leadPayload);

    try {
      const response = await api.put(API_ENDPOINTS.LEAD_BY_ID(leadId), leadPayload);
      console.log('✅ Backend response:', response.data);
      console.log('📅 Updated lead date in response:', response.data.lead?.date);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error updating lead:', error);
      console.error('Error response:', error.response?.data);
      return { success: false, error: error.response?.data?.message || 'Failed to update lead. Please ensure the leads API is deployed.' };
    }
  }

  // Delete a lead
  static async deleteLead(leadId) {
    try {
      await api.delete(API_ENDPOINTS.LEAD_BY_ID(leadId));

      // Import notification service and clean up notifications for this lead
      const notificationService = (await import('./notificationService')).default;
      notificationService.deleteNotificationsForLead(leadId);

      return { success: true };
    } catch (error) {
      console.error('Error deleting lead:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to delete lead. Please ensure the leads API is deployed.' };
    }
  }

  // Update lead status
  static async updateLeadStatus(leadId, status) {
    try {
      const response = await api.patch(API_ENDPOINTS.LEAD_BY_ID(leadId), {
        status: status,
        lastContact: new Date().toISOString()
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating lead status:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to update lead status' };
    }
  }

  // Search leads
  static async searchLeads(searchTerm) {
    try {
      const response = await api.get(`${API_ENDPOINTS.LEADS}?search=${encodeURIComponent(searchTerm)}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error searching leads:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to search leads' };
    }
  }

  // Get leads by status
  static async getLeadsByStatus(status) {
    try {
      const response = await api.get(`${API_ENDPOINTS.LEADS}?status=${status}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching leads by status:', error);
      return { success: false, error: error.response?.data?.message || 'Failed to fetch leads by status' };
    }
  }
}

// Default export for backward compatibility
export default LeadsService;
